type RouterEvents = {
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
};

type RouterModule = {
  default: {
    events?: RouterEvents;
  };
};

const LOCATION_EVENT = 'freestar:locationchange';

let historyPatched = false;
let listenerCount = 0;
let routerOff: (() => void) | undefined;
let routerAttachEpoch = 0;

const listeners = new Set<() => void>();

export const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

const notifyLocationChange = () => {
  if (typeof window === 'undefined') return;
  queueMicrotask(() => window.dispatchEvent(new Event(LOCATION_EVENT)));
};

const patchHistoryOnce = () => {
  if (typeof window === 'undefined' || historyPatched) return;
  historyPatched = true;

  const { pushState, replaceState } = window.history;
  window.history.pushState = function pushStatePatched(...args) {
    const result = pushState.apply(this, args as Parameters<History['pushState']>);
    notifyLocationChange();
    return result;
  };
  window.history.replaceState = function replaceStatePatched(...args) {
    const result = replaceState.apply(this, args as Parameters<History['replaceState']>);
    notifyLocationChange();
    return result;
  };
};

const attachRouterListener = (epoch: number, importRouter: () => Promise<RouterModule>) => {
  void importRouter()
    .then(module => {
      if (epoch !== routerAttachEpoch || listenerCount === 0) return;

      const routerEvents = module.default?.events;
      if (!routerEvents?.on || !routerEvents?.off) return;

      routerEvents.on('routeChangeComplete', notifyListeners);
      routerOff = () => routerEvents.off('routeChangeComplete', notifyListeners);
    })
    .catch(() => undefined);
};

const detachRouterListener = () => {
  routerAttachEpoch += 1;
  routerOff?.();
  routerOff = undefined;
};

export const subscribeFreestarPathname = (
  onStoreChange: () => void,
  importRouter: () => Promise<RouterModule> = () => import('next/router') as Promise<RouterModule>,
) => {
  patchHistoryOnce();
  listeners.add(onStoreChange);

  listenerCount += 1;

  if (listenerCount === 1) {
    window.addEventListener(LOCATION_EVENT, notifyListeners);
    window.addEventListener('popstate', notifyListeners);
    attachRouterListener(++routerAttachEpoch, importRouter);
  }

  return () => {
    listeners.delete(onStoreChange);
    listenerCount -= 1;

    if (listenerCount === 0) {
      window.removeEventListener(LOCATION_EVENT, notifyListeners);
      window.removeEventListener('popstate', notifyListeners);
      detachRouterListener();
    }
  };
};

export const getFreestarPathname = () => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

export const normalizeFreestarPathname = (value: string) => value.split('?')[0]?.split('#')[0] || '';

/** Test-only: reset module singletons between specs. */
export const resetFreestarPathnameStoreForTests = () => {
  listeners.clear();
  listenerCount = 0;
  routerOff?.();
  routerOff = undefined;
  routerAttachEpoch = 0;
  historyPatched = false;
};
